import _ from "lodash";
import React, {
  useState,
  useEffect
} from "react";
import { useCombobox } from "downshift";
import styled, { css } from "styled-components";

import Grid from "src/library/components/Hedron";
import { Text } from "src/library/components/Text";
import { IOrderLine } from "src/core/orders/api/orders.api";
import { IProduct } from "src/core/products/api/products.api";
import { ProductSearchComponent } from "src/core/products/components/ProductSearchComponent";

import { useCreditRequestContext } from "../context/CreditRequestContext";
import { useCreditReasonCodes } from "../hooks/useCreditReasonCodes";
import { CreditReasonSelect } from "./CreditReasonSelect";

interface CreditOrderLineSelectionProps {
  unselectedOrderLines: IOrderLine[];
  showApprovalColumn: boolean;
}

/**
 * Component for selecting an order line to report problems on
 * */
export const CreditOrderLineSelection: React.ReactFCWithChildren<CreditOrderLineSelectionProps> = ({ unselectedOrderLines, showApprovalColumn }) => {
  const creditReasons = useCreditReasonCodes();
  const [ , { setCreditRequestRow } ] = useCreditRequestContext();
  const [ selectedOrderLineId, setSelectedOrderLineId ] = useState(0);
  const [ searchTerm, setSearchTerm ] = useState("");
  const [ filteredAvailableProducts, setFilteredAvailableProducts ] = useState<IProduct[]>([]);

  const orderLine = React.useMemo(() => {
    return _.find(unselectedOrderLines, ol => ol.id === selectedOrderLineId);
  }, [ selectedOrderLineId, unselectedOrderLines ]);

  // List containing only the available products of all unselected order lines
  const availableProducts = React.useMemo(() => {
    return unselectedOrderLines.map(ol =>
      ol.product);
  }, [ unselectedOrderLines ]);

  /**
   * Finds the order line id that matches the given product
   * @param product
   * @returns order line id
   */
  const findOrderLineId = (product?: IProduct | null): number => {
    if (product) {
      const selectedOrderLine = _.find(unselectedOrderLines, ol => ol.product === product);

      return selectedOrderLine?.id || 0;
    } else return 0;
  };

  const previousDownShiftProps = useCombobox<IProduct>({
    items: filteredAvailableProducts.length ? filteredAvailableProducts : availableProducts,
    itemToString: () => "",
    onSelectedItemChange: ({ selectedItem }) => {
      setSearchTerm("");
      const orderLineId = findOrderLineId(selectedItem);

      setSelectedOrderLineId(orderLineId);
    }
  });

  /**
   * Filter out all available products based on the current search term
   */
  useEffect(() => {
    if (searchTerm.length === 0) {
      setFilteredAvailableProducts(availableProducts);
    } else {
      const filtered = availableProducts.filter(product => {
        const formattedDescription = product.description.toLowerCase();
        const formattedPartNumber = product.partNumber?.toLowerCase() || "";
        const formattedSearchTerm = searchTerm.toLowerCase();

        return formattedDescription.includes(formattedSearchTerm) || formattedPartNumber.includes(formattedSearchTerm);
      });

      setFilteredAvailableProducts(filtered);
    }
  }, [ searchTerm, availableProducts ]);

  return (
    <>
      <CreditOrderLineSelectionWrapper>
        {!orderLine ? (
          <SearchComponentWrapper>
            <ProductSearchComponent
              error={null}
              loading={false}
              minimumChars={0}
              onSearch={setSearchTerm}
              searchResults={filteredAvailableProducts}
              downshiftProps={previousDownShiftProps}
              placeholder="Select a product"
            />
          </SearchComponentWrapper>
        ) : null}

        {orderLine && creditReasons && (
          <>
            <OrderLineName>
              <Text>
                {`${orderLine.product.partNumber} - ${orderLine.product.description}`}
              </Text>
            </OrderLineName>

            <CreditReasonSelect
              orderLine={orderLine}
              availableCreditReasons={creditReasons}
              showApprovalColumn={showApprovalColumn}
              onAddCreditReason={(creditRequestReasonCodeId: number) => {
                setCreditRequestRow({
                  orderLineId: orderLine.id as number,
                  creditRequestReasonCodeId,
                  quantityRequested: 0
                }, orderLine);
                setSelectedOrderLineId(0);
              }}
            />
          </>
        )}
      </CreditOrderLineSelectionWrapper>
    </>
  );
};

const CreditOrderLineSelectionWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.lightGrey};
    border-bottom: 1px solid ${theme.colors.lightGrey};
    padding: ${theme.spacing.padding.md};
    align-items: center;
  `}
`;

const OrderLineName = styled(Grid.Bounds)`
  ${({ theme }) => css`
    border-radius: ${theme.borderRadius};
    border: 1px solid ${theme.colors.lightGrey};
    padding: ${theme.spacing.padding.md};
    background-color: ${theme.colors.white}
  `}
`;

const SearchComponentWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing.margins.md};
    width: 250px;
  `}
`;
